import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Button,
  Box,
  Typography,
  IconButton,
  LinearProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Close,
  Delete,
  CloudUpload,
  CheckCircle,
} from '@mui/icons-material';

interface Certificate {
  vin?: string;
  request_id?: string;
}

interface AssignToL2Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: AssignToL2Data) => void;
  certificate: Certificate | null;
}

export interface AssignToL2Data {
  category: string;
  subCategory: string;
  remarks: string;
  file?: File | null;
}

const categoryOptions = [
  {
    value: 'Device Issues', label: 'Device Issues', subCategories: [
      'Hardware related issues',
      'Zero Lat & Long Issue',
      'Live Data Not receiving',
      'Device Replacement Initiated',
      'Device Power off',
      'Physical Inspection Required'
    ]
  },
  {
    value: 'SIM Issues', label: 'SIM Issues', subCategories: [
      'SIM not activated',
      'No response to SMS from device',
      'State backend Validity issue',
      'State backend Whitelisting issue',
      'Top up Issue',
      'SIM not responding',
      'No SMS acknowledgement'
    ]
  },
  {
    value: 'Portal Issues', label: 'Portal Issues', subCategories: [
      'State back end Server down',
      'VAHAAN data unavailable',
      'SIM info unavailable on state backend',
      'OTP undelivered',
      'Incorrect tagging',
      'External Vaahan Tagging to be removed',
      'SMS acknowledgement pending'
    ]
  },
  {
    value: 'State Wise Issues', label: 'State Wise Issues', subCategories: [
      'Vehicle out of vicinity',
      'Emergency switch not engaged',
      'Hooter not working',
      'Device location outside RTO State'
    ]
  },
  {
    value: 'External Support', label: 'External Support', subCategories: [
      'Ignition On support Required',
      'No response from Dealer/Customer',
      'Customer not supporting for OTP',
      'AIS request rejection to be initiated',
      'VIN IMEI mapping wrong'
    ]
  },
  {
    value: 'L1 to L2', label: 'L1 to L2', subCategories: [
      'Device not connected to State Backend Server'
    ]
  },
];

const AssignToL2: React.FC<AssignToL2Props> = ({ open, onClose, onSubmit, certificate }) => {
  const [category, setCategory] = useState('');
  const [subCategory, setSubCategory] = useState('');
  const [remarks, setRemarks] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  const handleCategoryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCategory(event.target.value);
    setSubCategory(''); // Reset subcategory when category changes
  };

  const handleSubCategoryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSubCategory(event.target.value);
  };

  const handleRemarksChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRemarks(event.target.value);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      setSelectedFile(event.target.files[0]);
      setUploadSuccess(false);
    }
  };

  const handleCancel = () => {
    setCategory('');
    setSubCategory('');
    setRemarks('');
    setSelectedFile(null);
    setUploadSuccess(false);
    onClose();
  };

  const handleSubmit = async () => {
    if (!category || !subCategory) {
      setSnackbarMessage('Please select both category and sub-category');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    setIsUploading(true);

    try {
      // Simulate file upload process
      await new Promise(resolve => setTimeout(resolve, 1500));

      onSubmit({
        category,
        subCategory,
        remarks,
        file: selectedFile
      });

      setUploadSuccess(true);
      setSnackbarMessage('Ticket assigned successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);

      // Close after success
      setTimeout(() => {
        handleCancel();
      }, 1000);
    } catch (error) {
      setSnackbarMessage('Failed to assign ticket. Please try again.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setIsUploading(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const selectedCategory = categoryOptions.find(opt => opt.value === category);

  return (
    <>
      <Dialog open={open} onClose={handleCancel} fullWidth maxWidth="sm">
        <DialogTitle sx={{ position: 'relative', pr: 5 }}>
          Assign Ticket
          <IconButton
            onClick={handleCancel}
            size="small"
            sx={{ color: 'inherit', position: 'absolute', right: 15, top: 15 }}
            disabled={isUploading}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box mt={2}>
            {/* Vin row */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Vin
              </Typography>
              <Typography variant="body1" sx={{ color: '#222', fontSize: 16, p: 1, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                {certificate?.vin || 'N/A'}
              </Typography>
            </Box>

            {/* Request ID row */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Request ID
              </Typography>
              <Typography variant="body1" sx={{ color: '#222', fontSize: 16, p: 1, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                {certificate?.request_id
                  || 'N/A'}
              </Typography>
            </Box>

            {/* Category row */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Category
                <Typography component="span" color="error" sx={{ ml: 0.5 }}>*</Typography>
              </Typography>
              <TextField
                select
                fullWidth
                value={category}
                onChange={handleCategoryChange}
                variant="outlined"
                size="small"
                disabled={isUploading}
              >
                <MenuItem value="">
                  <em>Select the Category</em>
                </MenuItem>
                {categoryOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            {/* Sub Category row */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Sub Category
                <Typography component="span" color="error" sx={{ ml: 0.5 }}>*</Typography>
              </Typography>
              <TextField
                select
                fullWidth
                value={subCategory}
                onChange={handleSubCategoryChange}
                variant="outlined"
                size="small"
                disabled={!category || isUploading}
              >
                <MenuItem value="">
                  <em>Select the Sub Category</em>
                </MenuItem>
                {selectedCategory?.subCategories.map((subCat) => (
                  <MenuItem key={subCat} value={subCat}>
                    {subCat}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            {/* Remarks row */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Remarks
              </Typography>
              <TextField
                autoFocus
                margin="dense"
                id="remarks"
                placeholder="Remarks"
                type="text"
                fullWidth
                variant="outlined"
                value={remarks}
                multiline
                rows={3}
                onChange={handleRemarksChange}

              />
            </Box>

            {/* Upload File row */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Upload File
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  border: '1px solid #ccc',
                  borderRadius: 2,
                  px: 2,
                  py: 1,
                  background: '#fff',
                  minHeight: 56,
                }}
              >
                <input
                  id="ticket-file-upload"
                  type="file"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  style={{ display: 'none' }}
                  onChange={handleFileChange}
                  disabled={isUploading}
                />
                <label htmlFor="ticket-file-upload">
                  <Button
                    variant="contained"
                    component="span"
                    sx={{
                      background: '#000',
                      color: '#fff',
                      textTransform: 'none',
                      mr: 2,
                      minWidth: 120,
                      '&:hover': { background: '#333' }
                    }}
                    disabled={isUploading}
                  >
                    Choose File
                  </Button>
                </label>
                {selectedFile ? (
                  <>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ color: '#222', fontSize: 16 }}>
                        {selectedFile.name}
                      </Typography>
                      {uploadSuccess && (
                        <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                          <CheckCircle fontSize="small" sx={{ color: 'green' }} />
                          <Typography variant="body2" color="green">
                            File uploaded successfully
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    <IconButton
                      onClick={() => {
                        setSelectedFile(null);
                        setUploadSuccess(false);
                      }}
                      sx={{ color: '#E60012' }}
                      title="Remove file"
                      disabled={isUploading}
                    >
                      <Delete />
                    </IconButton>
                  </>
                ) : (
                  <>
                    <Typography sx={{ flex: 1, color: '#888', fontSize: 16 }}>
                      No file chosen
                    </Typography>
                    <IconButton disabled>
                      <CloudUpload sx={{ color: '#888' }} />
                    </IconButton>
                  </>
                )}
              </Box>
            </Box>
          </Box>
          {isUploading && <LinearProgress sx={{ mt: 2 }} />}
        </DialogContent>
        <DialogActions>
          <Button variant='outlined' onClick={handleCancel} disabled={isUploading}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!category || !subCategory || isUploading}
            color='error'
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert severity={snackbarSeverity} onClose={handleSnackbarClose} variant="filled">
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AssignToL2;